import type { Log } from "../../log/Log";
import type { Container } from "pixi.js";
import type { GridEffect } from "../GridEffect";
import type { GridCells } from "../../view/grid/GridLayer";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { WinAmountLabelTuningBase } from "../win-amount-label/WinAmountLabelTuningBase";
import type { SymbolWinEffect } from "../symbol-win/SymbolWinEffect";
import { SymbolDimSession, type SymbolDimExposurePort } from "./SymbolDimSession";
import { WinAmountLabels, type WinAmountLabelFactory } from "./WinAmountLabels";
import type { WinHighlightTuning } from "./WinHighlightTuning";

/** @see the game's `winPresentationTuning` — the canonical source of timing values. This layer holds no defaults of its own. */
export type WinHighlightEffectTimings = WinHighlightTuning;

/**
 * The win highlight, driven by the `grid_win_highlight` action: the non-winners dim, the winners
 * play their animation left to right on a stagger, the amount label shows, and the line's beat is
 * awaited (a slam finishes the pacing early through the caller's scope). The dim session and the
 * labels are collaborators; this layer sequences them per line and ends them with the round — or,
 * with `labelCollect`, sends the substep label to the collect target when the game asks.
 */
export class WinHighlightEffect implements GridEffect {
  private readonly cells: GridCells;
  private readonly log: Log;
  private readonly timings: WinHighlightEffectTimings;
  private readonly labelTuning: WinAmountLabelTuningBase;
  private readonly runner: PresentationTaskRunner;
  private readonly dim: SymbolDimSession;
  private readonly labels: WinAmountLabels | null;
  private readonly symbolWinEffect: SymbolWinEffect | undefined;
  /** Fired once per highlighted line (a mechanic's payline stroke). */
  private readonly onLineHighlighted: ((positionMask: string, winAmount?: string, scopeId?: string, lineMasks?: string[]) => void) | undefined;
  /** Bumped by each teardown; the engine-time stagger loop bails when it changes. */
  private staggerGeneration = 0;

  public constructor(input: {
    gridView: GridCells;
    log: Log;
    moneyFormatter: { formatMinor(amount: string, options?: { omitSymbol?: boolean }): string };
    /** Builds a win-amount label per highlight; absent = no amount labels. */
    createWinAmountLabel?: WinAmountLabelFactory | null;
    overlayRoot: Container;
    timings: WinHighlightEffectTimings;
    labelTuning: WinAmountLabelTuningBase;
    symbolWinEffect?: SymbolWinEffect;
    taskRunner: PresentationTaskRunner;
    /** The per-sprite exposure owner; absent = no symbol dim (the labels and the winner animation still play). */
    exposureOwner?: SymbolDimExposurePort;
    /** Where a collected label's digits land (a win meter's amount); with `timings.labelCollect` the substep label waits for `collectActiveLabels`. */
    labelCollectTarget?: Container | null;
    onLineHighlighted?: (positionMask: string, winAmount?: string, scopeId?: string, lineMasks?: string[]) => void;
  }) {
    this.cells = input.gridView;
    this.log = input.log;
    this.timings = input.timings;
    this.labelTuning = input.labelTuning;
    this.runner = input.taskRunner;
    this.symbolWinEffect = input.symbolWinEffect;
    this.onLineHighlighted = input.onLineHighlighted;
    this.dim = new SymbolDimSession(input.gridView, input.exposureOwner, input.timings, input.taskRunner);
    this.labels = input.createWinAmountLabel
      ? new WinAmountLabels({ cells: input.gridView, overlayRoot: input.overlayRoot, runner: input.taskRunner, create: input.createWinAmountLabel, moneyFormatter: input.moneyFormatter, tuning: input.timings, labelTuning: input.labelTuning, collectTarget: input.labelCollectTarget ?? null })
      : null;
  }

  /** The removal ends the session: leftover labels flush (a collected one waits for its collect), the dim fades back before the symbols vanish (awaited). */
  public async removeSymbolsByMask(_positionMask: string): Promise<void> {
    this.symbolWinEffect?.stopAll();
    this.staggerGeneration += 1;
    this.dim.clearRevealed();
    this.labels?.flush();
    await this.dim.end();
  }

  /**
   * The spin-start teardown for a mechanic with no removal (the reels): labels flush and the dim
   * ends at once, without reverting the exposure on screen — the pool resets it off-screen as the
   * scrolled-out views rebind, so the dimmed symbols do not snap bright as the reels start.
   */
  public clearForNewSpin(): void {
    this.symbolWinEffect?.stopAll({ revert: false });
    this.staggerGeneration += 1;
    this.dim.clearRevealed();
    this.labels?.flush(true);
    void this.dim.end({ immediate: true, keepMaterial: true });
  }

  /**
   * A win that stays on the grid, cleared where it stands (a feature's exit, before its finished panel; decision 65):
   * the winners' effects stop and revert, the labels go, the dim and this round's reveals are released at once. Unlike
   * `clearForNewSpin`, nothing scrolls the cells away to reset them, so they are restored here.
   */
  public clearStandingWin(): void {
    this.symbolWinEffect?.stopAll();
    this.staggerGeneration += 1;
    this.dim.clearRevealed();
    this.labels?.flush(true);
    void this.dim.end({ immediate: true });
  }

  /**
   * One winning line. `scopeId` is the caller's runner scope for the PACING (the winner stagger, the
   * line's beat, the label lead-in), which a slam finishes early; the dim and the flourishes are not scoped.
   */
  public async highlightWinByMask(positionMask: string, winAmount?: string, scopeId?: string, lineMasks?: string[], labelBreakdown?: { base: string; multiplier: string }, opts?: { deferBreakdownMerge?: boolean }): Promise<void> {
    const indices = this.cells.maskToIndices(positionMask);
    this.log(`[WinHighlight] cells=${indices.length}`);
    if (indices.length === 0) return;
    const t = this.timings;
    this.dim.onWin(positionMask, new Set(indices));
    const sweepSeconds = this.animateWinners(indices, scopeId);
    this.onLineHighlighted?.(positionMask, winAmount, scopeId, lineMasks);
    if (winAmount) this.labels?.onWin(indices, winAmount, scopeId, labelBreakdown, opts?.deferBreakdownMerge);
    // The line's beat, at least the cycle; with `awaitSymbolWinAnimations` as long as the winners actually play.
    const beatSeconds = t.awaitSymbolWinAnimations ? Math.max(t.cycleDurationMilliseconds / 1000, sweepSeconds) : t.cycleDurationMilliseconds / 1000;
    if (beatSeconds > 0) await this.runner.sleep(beatSeconds, scopeId);
    // A breakdown merged now runs longer; a deferred one has not happened yet.
    const multiplier = this.labelTuning.multiplier;
    if (labelBreakdown && multiplier && !opts?.deferBreakdownMerge) await this.runner.sleep(multiplier.holdSeconds + multiplier.popSeconds);
  }

  /**
   * The breathing between re-shows of a spin's wins (a title's idle win cycle): the dimmed cells fade back and the dim
   * session ends, so the next highlight dims afresh. The labels, the winners' effects and the cells revealed this round
   * stay as they are (decision 62).
   */
  public undimNonWinners(): Promise<void> {
    return this.dim.end();
  }

  /** The deferred merge, when the multiplier lands (a cannon impact). */
  public mergeActiveWinLabels(): void {
    this.labels?.mergeActive();
  }

  /**
   * `labelCollect`: the substep label flies into the collect target. Resolves true as it arrives (the target counts
   * then), false at once when no label is up to send. Its pacing runs under `scopeId`, which a slam finishes.
   */
  public collectActiveLabels(scopeId?: string): Promise<boolean> {
    return this.labels?.collect(scopeId) ?? Promise.resolve(false);
  }

  /** Specific cells lit mid-session (a scatter collect after a win). */
  public undimCellsAtIndices(indices: number[]): void {
    this.dim.revealCells(indices);
  }

  /** The winners' animation left to right, then top to bottom, on the stagger. Returns the sweep length. */
  private animateWinners(indices: number[], scopeId?: string): number {
    const effect = this.symbolWinEffect;
    if (!effect) return 0;
    const staggerSeconds = Math.max(0, this.timings.symbolAnimStaggerMilliseconds ?? 0) / 1000;
    // Pixi y grows down: "top first" is the smaller y.
    const ordered = indices.map((index) => ({ index, pos: this.cells.getCellLocalPosition(index) })).sort((a, b) => a.pos.x - b.pos.x || a.pos.y - b.pos.y);
    if (ordered.length === 0) return 0;
    let sweepSeconds = 0;
    if (effect.getPlaySeconds) {
      ordered.forEach(({ index }, k) => {
        const symbolId = this.cells.getSymbolViewAtIndex(index)?.getSymbolId() ?? "";
        sweepSeconds = Math.max(sweepSeconds, staggerSeconds * k + effect.getPlaySeconds!(symbolId));
      });
    }
    const dimWinners = !!this.timings.dimWinners;
    const play = (index: number): void => {
      const view = this.cells.getSymbolViewAtIndex(index);
      if (!view) return;
      effect.play({ symbolId: view.getSymbolId() ?? "", cellNode: view.node, symbolSprite: view.symbolSprite, index, onShineStart: dimWinners ? () => this.dim.revealByShine(index) : undefined });
    };
    play(ordered[0]!.index);
    if (staggerSeconds <= 0) {
      for (let k = 1; k < ordered.length; k++) play(ordered[k]!.index);
      return sweepSeconds;
    }
    const generation = this.staggerGeneration;
    void (async () => {
      for (let k = 1; k < ordered.length; k++) {
        await this.runner.sleep(staggerSeconds, scopeId);
        if (this.staggerGeneration !== generation) return;
        play(ordered[k]!.index);
      }
    })();
    return sweepSeconds;
  }
}
